import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Shell from "../components/Shell";
import { api, subjectMeta, errMsg, getRole } from "../lib/api";
import { CheckCircle, XCircle, MinusCircle, EnvelopeSimple, Lightning, ArrowRight, LockKey, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_META = {
  correct: { label: "Doğru", color: "#2A9D8F", bg: "#E9F5F3", Icon: CheckCircle },
  incorrect: { label: "Yanlış", color: "#E76F51", bg: "#FCECE8", Icon: XCircle },
  blank: { label: "Boş", color: "#8D99AE", bg: "#F1F2F4", Icon: MinusCircle },
};

export default function Result() {
  const { id } = useParams();
  const nav = useNavigate();
  const [sub, setSub] = useState(null);
  const [genLoading, setGenLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const role = getRole();
  const isParent = role === "parent";

  const load = async () => {
    try {
      const r = await api.get(`/submissions/${id}`);
      setSub(r.data);
    } catch { toast.error("Sonuç bulunamadı"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Auto refresh every 10s while locked, to detect parent unlock
  useEffect(() => {
    if (!sub || sub.explanations_unlocked) return;
    const t = setInterval(() => load(), 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [sub?.explanations_unlocked]);

  const manualRefresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  const generateTest = async () => {
    setGenLoading(true);
    try {
      const { data } = await api.post("/tests/generate", { subject: sub.subject }, { timeout: 120000 });
      nav(`/app/test/${data.id}`);
    } catch (e) {
      toast.error(errMsg(e, "Test üretilemedi"));
    } finally {
      setGenLoading(false);
    }
  };

  if (!sub) {
    return (
      <Shell title="Sonuçlar yükleniyor..."><div className="p-6 text-brand-ink2">Yükleniyor...</div></Shell>
    );
  }

  const sm = subjectMeta(sub.subject);
  const dateStr = (() => {
    try { return new Date(sub.created_at).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" }); }
    catch { return ""; }
  })();
  const emailLabel = {
    sent: "Veliye e-posta gönderildi",
    mocked: "Rapor hazır (e-posta mock modda)",
    failed: "E-posta gönderilemedi",
    pending: "E-posta gönderiliyor...",
  }[sub.email_status] || "Rapor hazırlandı";

  return (
    <Shell title={`${sm.name} Ödevi · ${dateStr}`} hideNav={isParent}>
      <div className="rounded-3xl p-5 mb-5 text-white" style={{ background: "#1D3557" }} data-testid="result-summary">
        <div className="text-xs uppercase tracking-widest font-nunito font-semibold text-white/60">Ödev özeti</div>
        <div className="font-outfit font-extrabold text-2xl mt-1">{sm.name}</div>
        <div className="grid grid-cols-3 gap-3 mt-4">
          <div className="rounded-2xl py-3 text-center" style={{ background: "rgba(42,157,143,0.25)" }}>
            <div className="font-outfit font-extrabold text-2xl text-good" data-testid="result-total-correct">{sub.correct}</div>
            <div className="text-xs font-nunito text-white/80">Doğru</div>
          </div>
          <div className="rounded-2xl py-3 text-center" style={{ background: "rgba(231,111,81,0.25)" }}>
            <div className="font-outfit font-extrabold text-2xl text-bad" data-testid="result-total-wrong">{sub.incorrect}</div>
            <div className="text-xs font-nunito text-white/80">Yanlış</div>
          </div>
          <div className="rounded-2xl py-3 text-center" style={{ background: "rgba(141,153,174,0.30)" }}>
            <div className="font-outfit font-extrabold text-2xl text-blank" data-testid="result-total-blank">{sub.blank}</div>
            <div className="text-xs font-nunito text-white/80">Boş</div>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-2 text-sm font-nunito text-white/85" data-testid="email-status">
          <EnvelopeSimple size={18} weight="duotone" /> {emailLabel}
        </div>
      </div>

      {sub.weak_topics?.length > 0 && (
        <section className="mb-5">
          <div className="text-xs uppercase tracking-widest font-nunito font-extrabold text-bad mb-2">Zayıf konular</div>
          <div className="flex flex-wrap gap-2" data-testid="weak-topics">
            {sub.weak_topics.map((t, i) => (
              <span key={i} className="px-3 py-1.5 rounded-full bg-bad-bg font-nunito font-semibold text-sm text-bad">{t}</span>
            ))}
          </div>
        </section>
      )}

      {!isParent && !sub.explanations_unlocked && (sub.incorrect + sub.blank) > 0 && (
        <div className="mb-5 p-4 rounded-2xl bg-brand-ink text-white flex items-start gap-3" data-testid="lock-banner">
          <div className="w-10 h-10 rounded-2xl bg-brand-primary flex items-center justify-center flex-shrink-0">
            <LockKey size={22} weight="duotone" color="#1D3557" />
          </div>
          <div className="flex-1">
            <div className="font-outfit font-bold text-base">Çözümler veli onayında</div>
            <div className="font-nunito text-sm text-white/80 mt-1">
              Doğru cevaplar ve çözümler şu an gizli. Anne veya baban e-postadaki butona tıklayınca açılır.
            </div>
            <button
              onClick={manualRefresh}
              disabled={refreshing}
              data-testid="refresh-unlock-button"
              className="mt-3 inline-flex items-center gap-2 bg-white/10 hover:bg-white/15 transition-colors text-white text-sm font-nunito font-semibold px-3 py-1.5 rounded-full"
            >
              <ArrowsClockwise size={16} weight={refreshing ? "fill" : "duotone"} className={refreshing ? "animate-spin" : ""} />
              {refreshing ? "Kontrol ediliyor..." : "Açıldı mı, kontrol et"}
            </button>
          </div>
        </div>
      )}

      {!isParent && (
        <button
          data-testid="generate-test-button"
          onClick={generateTest}
          disabled={genLoading}
          className="w-full bg-brand-primary text-brand-ink font-outfit font-bold text-lg py-4 rounded-full shadow-[0_4px_18px_rgba(255,183,3,0.45)] disabled:opacity-50 flex items-center justify-center gap-2 mb-6"
        >
          <Lightning size={20} weight="fill" /> {genLoading ? "Test hazırlanıyor..." : "10 soruluk test çıkart"}
        </button>
      )}

      {sub.parent_note && (
        <div className="mb-5 p-4 rounded-2xl bg-[#FEF9C3] font-nunito text-brand-ink" data-testid="parent-note">
          <div className="font-outfit font-bold text-sm mb-1">Veliye not</div>
          {sub.parent_note}
        </div>
      )}

      <section>
        <div className="text-xs uppercase tracking-widest font-nunito font-semibold text-brand-ink2 mb-2">Soru bazında</div>
        <div className="flex flex-col gap-3" data-testid="question-breakdown">
          {sub.questions.map((q, i) => {
            const m = STATUS_META[q.status] || STATUS_META.blank;
            return (
              <div
                key={i}
                className="bg-white rounded-2xl shadow-sm p-4 border-l-4"
                style={{ borderColor: m.color }}
                data-testid={`question-row-${q.question_no}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: m.bg }}>
                    <m.Icon size={20} weight="fill" color={m.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-outfit font-bold text-brand-ink">Soru {q.question_no}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full font-nunito font-semibold" style={{ background: m.bg, color: m.color }}>
                        {m.label}
                      </span>
                    </div>
                    {q.topic && <div className="text-xs font-nunito text-brand-ink2 mt-0.5">Konu: <b>{q.topic}</b></div>}
                    {q.question_text && <div className="text-sm font-nunito text-brand-ink mt-2 line-clamp-3">{q.question_text}</div>}
                    {q.status !== "correct" && (
                      sub.explanations_unlocked ? (
                        (q.explanation || q.correct_answer) && (
                          <div className="mt-3 p-3 rounded-xl text-sm font-nunito" style={{ background: m.bg, color: m.color }}>
                            {q.correct_answer && <div className="mb-1"><b>Doğru cevap:</b> {q.correct_answer}</div>}
                            {q.explanation && <div>{q.explanation}</div>}
                          </div>
                        )
                      ) : (
                        <div className="mt-3 p-3 rounded-xl text-sm font-nunito bg-brand-paperAlt text-brand-ink2 flex items-center gap-2" data-testid={`locked-${q.question_no}`}>
                          <LockKey size={16} weight="duotone" /> Çözüm veli onayında.
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <button
        onClick={() => nav(isParent ? "/parent" : "/app")}
        className="mt-8 w-full bg-brand-paperAlt text-brand-ink font-outfit font-semibold text-base py-3 rounded-full flex items-center justify-center gap-2"
        data-testid="back-home-button"
      >
        {isParent ? "Veli paneline dön" : "Ana sayfaya dön"} <ArrowRight size={18} />
      </button>
    </Shell>
  );
}
